import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useAssetsLoadedState } from '../../hooks/useAssetsLoadedState';
import { parseQuery } from '../../core/helpers/parseQuery';
import { DefaultQueryParams } from '../../core/constants/defaultQueryParams';
import { AppDefaultValuesEnum } from '../../core/constants/appDefaultValues';
import { iframeService } from '../../services/iframeService';
import { TranslationContext } from '../../contexts/translation';
import { translationService } from '../../services/translationService';
import { soundService } from '../../services/baseSoundService/soundService';
import { applicationStore } from '../../store/applicationStore';
import { preloadImagesAndDetectAllLoaded } from '../../core/helpers/preloadImagesAndDetectAllLoaded';
import winTopC from '../../assets/images/image-global/win.png';
import bigWinTopC from '../../assets/images/image-global/big-win.png';
import megaWinTopC from '../../assets/images/image-global/mega-win.png';
import megaWinTopC2 from '../../assets/images/image-global/mega-win-2.png';
import winButton from '../../assets/images/image-global/win-button.png';
import megaButton from '../../assets/images/image-global/mega-win-button.png';
import megaButton2 from '../../assets/images/image-global/mega-win-button-2.png';
import minWinPG from '../../assets/images/image-global/new-min-win.png';
import megaWinPG from '../../assets/images/image-global/new-mega-win.png';
import bigWinPG from '../../assets/images/image-global/new-big-win.png';
import { isPG } from '../../core/helpers/skinHelper';
import SkinLoader from '../../components/skinLoader';

export let bodyRef;

const gamesGlobalImages = isPG()
  ? [minWinPG, megaWinPG, bigWinPG]
  : [winTopC, bigWinTopC, megaWinTopC, megaWinTopC2, winButton, megaButton, megaButton2];

const Loading = observer(({ children }) => {
  const {
    isSocketConnected,
    isSoundsLoaded,
    setIsSoundsLoaded,
    isOverwriteMainCss,
    isOverwriteCoreCssLoaded,
    isOverwriteGameCssLoaded,
    partnerSkin,
    showSkinLoader,
    setShowSkinLoader,
  } = applicationStore;
  const { assetsIsLoaded } = useAssetsLoadedState(bodyRef);
  const [translations, setTranslations] = useState({});
  const [isImgLoaded, setIsImageLoaded] = useState(false);

  const query = parseQuery(window.location.search);
  const lang = query[DefaultQueryParams.Culture] || AppDefaultValuesEnum.LANGUAGE;
  const partnerId = query[DefaultQueryParams.PartnerId];

  const fetchTranslation = useCallback(async () => {
    await translationService
      .getTranslations({
        applicationId: AppDefaultValuesEnum.APPLICATION_ID,
        code: lang,
        partnerId,
      })
      .then((res) => {
        setTranslations(res.data.result);
      })
      .catch(() => {
        translationService
          .getTranslations({
            applicationId: AppDefaultValuesEnum.APPLICATION_ID,
            code: AppDefaultValuesEnum.LANGUAGE,
            partnerId,
          })
          .then((res) => {
            setTranslations(res.data.result);
          });
      });
  }, [lang, partnerId]);

  const fetchSound = useCallback(async () => {
    await soundService
      .init({ language: lang })
      .then(() => setIsSoundsLoaded(true))
      .catch(() => setIsSoundsLoaded(true)); // in case if there's no sounds
  }, [lang, setIsSoundsLoaded]);

  useEffect(() => {
    bodyRef = document.querySelector('body');
    (async function () {
      await fetchTranslation();
      await fetchSound();
    })();
  }, [fetchTranslation, fetchSound]);

  const additionalCssLoaded = useMemo(() => {
    return isOverwriteMainCss ? isOverwriteCoreCssLoaded || isOverwriteGameCssLoaded : true;
  }, [isOverwriteCoreCssLoaded, isOverwriteGameCssLoaded, isOverwriteMainCss]);

  useEffect(() => {
    preloadImagesAndDetectAllLoaded(gamesGlobalImages, (allLoaded) => {
      if (allLoaded) {
        setIsImageLoaded(true);
      }
    });
  }, []);

  useEffect(() => {
    if (
      isImgLoaded &&
      additionalCssLoaded &&
      assetsIsLoaded &&
      isSocketConnected &&
      isSoundsLoaded
    ) {
      if (partnerSkin && showSkinLoader) {
        setShowSkinLoader(false);
      } else {
        iframeService.sendLoadedState();
      }
    }
  }, [
    partnerSkin,
    showSkinLoader,
    setShowSkinLoader,
    isImgLoaded,
    assetsIsLoaded,
    translations,
    isSocketConnected,
    isSoundsLoaded,
    additionalCssLoaded,
  ]);

  return (
    <TranslationContext.Provider value={translations}>
      {!!partnerSkin && showSkinLoader && <SkinLoader partnerSkin={partnerSkin} />}
      {children}
    </TranslationContext.Provider>
  );
});
export default Loading;
